export type RegistrationPlanTier = 'grow' | 'scale' | 'pro'
export type RegistrationBillingInterval = 'monthly' | 'yearly'

export interface RegistrationPlanApiResponse {
  tier: RegistrationPlanTier
  name: string
  monthly_price_id: string | null
  yearly_price_id: string | null
  display?: {
    monthly_price?: string | null
    yearly_price?: string | null
    yearly_total?: string | null
    tagline?: string | null
    popular?: boolean | null
  } | null
}

export interface RegistrationPlanOption {
  tier: RegistrationPlanTier
  name: string
  monthlyPriceId: string | null
  yearlyPriceId: string | null
  display: {
    monthlyPrice: string
    yearlyPrice: string
    yearlyTotal: string | null
    tagline: string
    popular: boolean
  }
}

export interface RegistrationSelection {
  plan: RegistrationPlanTier
  billingInterval: RegistrationBillingInterval
}

export const REGISTRATION_PLAN_ORDER: RegistrationPlanTier[] = ['grow', 'scale', 'pro']
export const REGISTRATION_INTERVAL_ORDER: RegistrationBillingInterval[] = ['yearly', 'monthly']

export function normalizeRegistrationPlans(
  plans: RegistrationPlanApiResponse[]
): RegistrationPlanOption[] {
  return [...plans]
    .sort(
      (left, right) =>
        REGISTRATION_PLAN_ORDER.indexOf(left.tier) - REGISTRATION_PLAN_ORDER.indexOf(right.tier)
    )
    .map((plan) => ({
      tier: plan.tier,
      name: plan.name,
      monthlyPriceId: plan.monthly_price_id,
      yearlyPriceId: plan.yearly_price_id,
      display: {
        monthlyPrice: plan.display?.monthly_price ?? '',
        yearlyPrice: plan.display?.yearly_price ?? '',
        yearlyTotal: plan.display?.yearly_total ?? null,
        tagline: plan.display?.tagline ?? '',
        popular: Boolean(plan.display?.popular)
      }
    }))
}

export function planHasPrice(
  plan: RegistrationPlanOption,
  billingInterval: RegistrationBillingInterval
): boolean {
  return Boolean(getPlanPriceId(plan, billingInterval))
}

export function getPlanPriceId(
  plan: RegistrationPlanOption,
  billingInterval: RegistrationBillingInterval
): string | null {
  return billingInterval === 'yearly' ? plan.yearlyPriceId : plan.monthlyPriceId
}

export function getPlanByTier(
  plans: RegistrationPlanOption[],
  tier: RegistrationPlanTier | null | undefined
): RegistrationPlanOption | undefined {
  if (!tier) return undefined
  return plans.find((plan) => plan.tier === tier)
}
export function hasSelectableRegistrationPrices(plans: RegistrationPlanOption[]): boolean {
  return plans.some((plan) => Boolean(plan.monthlyPriceId) || Boolean(plan.yearlyPriceId))
}

export function resolveInitialRegistrationSelection(
  plans: RegistrationPlanOption[],
  requestedPlan: string | null | undefined,
  requestedInterval: string | null | undefined
): RegistrationSelection | null {
  const requestedTier = normalizeTier(requestedPlan)
  const requestedBillingInterval = normalizeInterval(requestedInterval)

  if (!requestedTier || !requestedBillingInterval) {
    return null
  }

  const requested = getPlanByTier(plans, requestedTier)
  if (!requested || !planHasPrice(requested, requestedBillingInterval)) {
    return null
  }

  return {
    plan: requestedTier,
    billingInterval: requestedBillingInterval
  }
}

export function normalizeTier(value: string | null | undefined): RegistrationPlanTier | null {
  if (value === 'grow' || value === 'scale' || value === 'pro') {
    return value
  }
  return null
}

export function normalizeInterval(
  value: string | null | undefined
): RegistrationBillingInterval | null {
  if (value === 'monthly' || value === 'yearly') {
    return value
  }
  return null
}

export function getFallbackSelectionForInterval(
  plans: RegistrationPlanOption[],
  billingInterval: RegistrationBillingInterval
): RegistrationSelection | null {
  for (const tier of REGISTRATION_PLAN_ORDER) {
    const plan = getPlanByTier(plans, tier)
    if (plan && planHasPrice(plan, billingInterval)) {
      return { plan: tier, billingInterval }
    }
  }

  return null
}

export function getFallbackSelectionForPlan(
  plan: RegistrationPlanOption
): RegistrationSelection | null {
  if (planHasPrice(plan, 'yearly')) {
    return { plan: plan.tier, billingInterval: 'yearly' }
  }

  if (planHasPrice(plan, 'monthly')) {
    return { plan: plan.tier, billingInterval: 'monthly' }
  }

  return null
}
